// 터치 조작: 좌측 플로팅 조이스틱, 우측 가장자리 2열/3열의 스킬 6칸·페이지·물약·공격·회피·각성.
// skillHudLayout.ts가 안전영역·44px 목표·중앙 시야 보호를 계산한다. 배치가 빈 칸이어도 다른 스킬의 자리는 움직이지 않는다.
// 천공 올려베기(Lv5)·진홍 초승달(Lv10)은 자동 습득 뒤에만 보인다. 쪽 표시 칩(1/2·다음 스킬 Lv)을 탭하거나 좌우로 쓸어 페이지를 넘긴다(docs/SKILL-TREE.md 11.3).
// 터치 기기(coarse pointer)이거나 ?touch=1일 때만 보인다. 포인터 이벤트로 멀티터치를 지원한다.
// 사각 스킬 원화·쿨타임 스윕·남은 초·MP 부족 표시는 PC 스킬 바(skillbar.ts)와 같은 부품과 표를 쓴다.
import { cameraResetKeyLabel, keymap } from '../shared/keymap';
import { effectiveMpCost } from '../shared/gear';
import { clampZoom, loadCameraPrefs } from '../shared/cameraPrefs';
import { Btn, SKILL_BTN_ALL, SKILL_MAX, Scene, combatScene, type BtnBit, type GameCommand, type GameView, type InputSink, type PlayableHeroId } from '../shared/types';
import { C, FONT, FONT_NUM, TEXT_OUTLINE, designScale, el } from './dom';
import { seventhTouchRect } from './rebirthSlot';
import { HUD_ICON_PX, iconOver } from './uiArt';
import { cellOfSkill, layoutKey, learnedMaskOf, nextSkillText, pageText, skillShown, slotsOf } from './slotsModel';
import { baseJoystickZone, hudBlockRect, joystickZones, villageHudLayout, type Rect } from './questCardModel';
import { growthOf, skillSpecOf } from './skillTreeModel';
import { AW_READY_ANIM, AW_SWEEP, CD_SWEEP, CD_TEXT, DIM_FILTER, MP_OK_CSS, READY_KF, READY_OPT, buildSkillFace, cdTenths, glintFace, isRefundCue, setCancelMark, setFaceHero, setMpBadge, sweepStep, type SkillFace } from './skillbar';
import type { GameEvent } from '../shared/events';
import { toView, viewW, viewH } from '../shared/viewport';
import { PotionHud } from './potionHud';
import { TOUCH_CUSTOM_IDS, applyTouchCustom, fixedHudRects, touchCustomBlocked, touchHudLayout, touchRectAt, touchRectOf, touchWithRect, type HudInsets, type HudRect, type TouchCustomId, type TouchCustomPos } from './skillHudLayout';
import { UI_SIZE_EVENT, uiSizeFactor } from './uiSize';

const STICK_R = 64;
/** 입력 원점은 손가락을 댄 곳이다. 그림의 안전영역 보정이 이동 방향을 바꾸지 않는다. */
export function floatingStickMotion(startX: number, startY: number, x: number, y: number, radius: number) {
  const dx = x - startX, dy = y - startY, len = Math.hypot(dx, dy);
  const factor = len > radius ? radius / len : 1;
  const nx = len < 8 ? 0 : dx * factor / radius;
  const ny = len < 8 ? 0 : dy * factor / radius;
  // 손잡이 전체가 링 안에 남아 중앙 시야로 넘어가지 않게 한다.
  const travel = Math.max(0, radius - 31);
  return { x: nx, y: ny, visualX: nx * travel, visualY: ny * travel };
}
/** 핀치: 손가락 사이 거리 비율 1당 줌 변화량(벌리면 가까이, 좁히면 넓게) */
const PINCH_GAIN = 1.5;

interface Ctl {
  f: SkillFace;
  mpText: HTMLDivElement | null;
  caption: HTMLDivElement | null;
  bit: BtnBit;
  /** 링 바깥 지름 = D × k */
  k: number;
  /** 공격 버튼 중심 기준 극좌표(각도°: 0 = 오른쪽, 90 = 위, 거리 배율). null이면 공격 자신 */
  polar: readonly [number, number] | null;
  skill: number;
  mpCost: number;
  lastCd: number;
  lastTenths: number;
  lastMpOk: number;
  lastReady: number;
  lastCancel: number;
  pointer: number;
  /** 습득 전이거나 현재 페이지에 없는 스킬 버튼은 보이지 않는다. 숨김이면 입력·갱신을 하지 않는다 */
  hidden: boolean;
  /** 스킬의 현재 페이지 칸(0..5). 스킬이 아니거나 안 보이면 -1 */
  cell: number;
}

const CHIP_H = 44;
/**
 * 쪽 표시 칩 모양. 다른 터치 버튼처럼 원(border-radius 50%)이어야 한다: 고객 배치 충돌 검사(touchCustomBlocked)가 버튼끼리를 원으로 재므로,
 * 칩이 둥근 사각형이면 원 검사를 통과한 배치에서 칩 모서리가 스킬 버튼 위를 덮어 스킬 탭이 쪽 넘김으로 먹혔다(PR #211 리뷰 P2)
 */
export const TOUCH_CHIP_CSS = 'position:absolute;display:none;box-sizing:border-box;height:' + CHIP_H + 'px;min-width:44px;padding:2px;border-radius:50%;overflow:hidden;border:2px solid ' + C.gold + ';' +
  'background:linear-gradient(180deg,rgba(38,51,90,.94),rgba(16,22,42,.94));box-shadow:0 2px 6px rgba(0,0,0,.65);flex-direction:column;align-items:center;justify-content:center;' +
  'pointer-events:auto;touch-action:none;user-select:none;white-space:nowrap;';
/** 칩을 쓸어 쪽을 지정하는 최소 가로 이동(px) */
const SWIPE_PX = 36;

export function isTouchDevice(params: URLSearchParams): boolean {
  if (params.get('touch') === '1') return true;
  if (params.get('touch') === '0') return false;
  return typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;
}

export class TouchControls {
  readonly root: HTMLDivElement;
  private readonly cameraReset: HTMLButtonElement | null;
  /** 조이스틱 입력 영역 조각(퀘스트 카드를 피해 최대 2개). 안 쓰는 조각은 숨긴다 */
  private readonly zones: HTMLDivElement[] = [];
  private zoneKey = '';
  private zoneVer = -2;
  private readonly base: HTMLDivElement;
  private readonly knob: HTMLDivElement;
  /** 환생 추가 칸(7번째) 자리. 없으면 null */
  private seventh: { x: number; y: number; w: number; h: number } | null = null;
  private readonly ctls: Ctl[] = [];
  private stickPointer = -1;
  private stickCx = 0;
  private stickCy = 0;
  private stickStartX = 0;
  private stickStartY = 0;
  private stickR = STICK_R;
  private stickMinX = 8;
  private stickMinY = 8;
  private stickBottom = 12;
  private scale = 1;
  private shown = false;
  /** 세로 화면에서 각성 게이지를 둘 자리(버튼 묶음 바로 위, 오른쪽 절반 가운데). on=false면 HUD 기본 자리(하단 중앙) */
  readonly gaugeAnchor = { on: false, x: 0, bottom: 0, width: 0 };
  /** 전투 버튼(공격·스킬·회피·각성) 표시 여부. 마을에서는 조이스틱만 둔다(코어가 전투 입력을 던전에서만 받는다). */
  private combatShown = true;
  private lastLayout = -1;
  private lastNextLv = -1;
  /** 버튼 이름·MP를 맞춘 영웅(c022). 바뀌면 relabel */
  private builtHero: PlayableHeroId = 'kael';
  /** 마지막으로 그린 전직 갈래(C10-6: 전직 스킬 이름·그림이 갈래마다 다르다) */
  private builtJob: 0 | 1 = 0;
  /** 물약 퀵슬롯 버튼 2개(D2c) */
  private readonly potions: PotionHud;
  /** 쪽 표시 칩(탭 = 전환, 좌우 쓸기 = 쪽 지정) */
  private readonly chip: HTMLDivElement;
  private readonly chipPage: HTMLSpanElement;
  private readonly chipNext: HTMLDivElement;
  private chipPointer = -1;
  private chipX0 = 0;
  /** 화면 크기·방향이 바뀔 때만 버튼 배치를 다시 계산한다 */
  private readonly onResize = (): void => { this.layout(); this.applyZones(); this.editorRefresh?.(); };
  /** 두 손가락 줌: 게임 화면(캔버스) 빈 곳에 닿은 손가락만 센다. 조이스틱·버튼 위 손가락은 제외 */
  private readonly pinchPts = new Map<number, { x: number; y: number }>();
  private pinchStartDist = 0;
  private pinchStartZoom = 0;
  private pinchZoom = Number.NaN; // 줌이 음수(근접)일 수 있어 -1 대신 NaN이 '핀치 값 없음'
  /** 지금 줌(HUD 버튼·휠·설정으로 바뀌면 따라 맞춘다) */
  private zoomNow = loadCameraPrefs().zoom;
  private readonly onZoomed = (e: Event): void => {
    const z = Number((e as CustomEvent).detail);
    if (Number.isFinite(z)) this.zoomNow = z;
  };

  /** 게임 이벤트 커서(c017 칼날의 박자 반짝임만 읽는다)와 이번 프레임 반짝임 요청 */
  private cursor = 0;
  private glintReq = false;
  private readonly onEvent = (e: GameEvent): void => { if (isRefundCue(e)) this.glintReq = true; };

  /** 고객 배치(화면 비율 중심)와 버튼 투명도(UX-FLOW 3절 터치 버튼 조절) */
  private customPos: TouchCustomPos = {};
  private opacity = 1;
  /** 마지막 자동 배치와 안전영역(편집기가 같은 기준으로 그린다) */
  private autoLayout: ReturnType<typeof touchHudLayout> | null = null;
  private safeNow: HudInsets = { left: 0, right: 0, top: 0, bottom: 0 };
  /** 고정 HUD(일시정지·카메라·미니맵·목표·자동 버튼) 사각형. 고객 배치 버튼이 덮지 못한다 */
  private fixedNow: HudRect[] = [];
  /** 편집기가 일시정지를 본 뒤 게임이 재개되면(Esc·패드 Start) 편집기를 닫는다 */
  private editorSawPause = false;
  private editorClose: (() => void) | null = null;
  private editor: HTMLDivElement | null = null;
  /** 편집기가 열려 있을 때 화면 크기·방향이 바뀌면 자동 배치·좌표를 다시 잡는다 */
  private editorRefresh: (() => void) | null = null;

  constructor(parent: HTMLElement, private readonly view: GameView, private readonly input: InputSink, scale: number, private readonly send?: (c: GameCommand) => void, custom?: { pos: TouchCustomPos; opacity: number }) {
    this.scale = scale;
    if (custom) { this.customPos = { ...custom.pos }; this.opacity = custom.opacity; }
    this.cursor = view.events.written;
    // HUD의 기존 줌 버튼 옆에 둔다. 터치 표시 여부와 무관하게 같은 컨테이너를 따른다.
    const zoomBox = parent.querySelector('[data-testid="camera-zoom"]')?.parentElement;
    // HUD 정리(2026-10-27): 글자 단추 대신 확대 단추 왼쪽 둥근 되돌리기 아이콘
    this.cameraReset = zoomBox ? el('button', 'position:absolute;right:50px;top:0;width:44px;height:44px;box-sizing:border-box;padding:0;border:1px solid rgba(255,255,255,.32);border-radius:50%;background:rgba(10,16,32,.55);color:#f4ecd8;cursor:pointer;pointer-events:auto;touch-action:manipulation;display:flex;align-items:center;justify-content:center;', zoomBox) : null;
    if (this.cameraReset) {
      // 그림: Codex image_gen 되돌리기 아이콘(btn-cam-reset). 받지 못하면 선 모양 화살표가 대신 보인다
      iconOver(el('div', 'width:' + HUD_ICON_PX + 'px;height:' + HUD_ICON_PX + 'px;display:grid;place-items:center;pointer-events:none;', this.cameraReset), 'btn-cam-reset', HUD_ICON_PX,
        '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12a8 8 0 1 0 3-6.2"/><path d="M4 4v4h4"/></svg>');
      this.cameraReset.setAttribute('aria-label', '카메라 초기화');
      this.cameraReset.type = 'button';
      this.cameraReset.dataset.testid = 'camera-reset';
      const camTitle = (): void => { if (this.cameraReset) this.cameraReset.title = '카메라 초기화 (' + cameraResetKeyLabel(keymap.preset) + ')'; };
      camTitle(); keymap.subscribe(camTitle);
      this.cameraReset.addEventListener('pointerdown', (e) => e.preventDefault());
      // 키보드로 버튼을 누를 때 Enter 대화·Space 각성이 함께 나가지 않게 한다.
      this.cameraReset.addEventListener('keydown', (e) => {
        if (e.code === 'Enter' || e.code === 'NumpadEnter' || e.code === 'Space') e.stopPropagation();
      });
      this.cameraReset.addEventListener('click', () => window.dispatchEvent(new CustomEvent('dh-camera-yaw', { detail: { reset: true } })));
    }
    this.root = el('div', 'position:absolute;inset:0;pointer-events:none;display:none;touch-action:none;padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);', parent);
    this.root.style.opacity = String(this.opacity);

    // 조이스틱 입력 영역(좌측 48% × 아래 72%). 마을 퀘스트 카드가 그 위에 있으면 카드를 피해 나눈다(applyZones).
    // 예전에는 영역 전체가 카드 아래에 깔려, 카드 줄을 누른 손가락이 조이스틱 대신 자동 이동을 눌렀다.
    for (let i = 0; i < 2; i++) {
      const z = el('div', 'position:absolute;pointer-events:auto;touch-action:none;display:none;', this.root);
      z.dataset.testid = 'joystick-zone-' + i;
      z.addEventListener('pointerdown', this.stickDown);
      z.addEventListener('pointermove', this.stickMove);
      z.addEventListener('pointerup', this.stickUp);
      z.addEventListener('pointercancel', this.stickUp);
      this.zones.push(z);
    }
    this.base = el('div', 'position:absolute;width:' + STICK_R * 2 + 'px;height:' + STICK_R * 2 + 'px;margin:' + -STICK_R + 'px 0 0 ' + -STICK_R + 'px;border-radius:50%;' +
      'border:3px solid rgba(242,193,78,.7);background:radial-gradient(circle,rgba(20,28,50,.25),rgba(20,28,50,.55));box-shadow:0 0 12px rgba(0,0,0,.5),inset 0 0 18px rgba(0,0,0,.5);display:none;', this.root);
    this.knob = el('div', 'position:absolute;width:58px;height:58px;margin:-29px 0 0 -29px;border-radius:50%;background:radial-gradient(circle at 35% 30%,#fff,#b8c0d8 55%,#5a6480);' +
      'border:2px solid rgba(242,193,78,.85);box-shadow:0 3px 8px rgba(0,0,0,.6);opacity:.9;display:none;', this.root);
    this.base.dataset.testid = 'touch-joystick-ring';
    this.knob.dataset.testid = 'touch-joystick-knob';

    // 버튼: 공격(가운데) 둘레에 피의 돌진(왼쪽 위)·칼날 폭풍(위)·붉은 낙화(오른쪽 위)·회피(왼쪽 아래)·각성(오른쪽 아래). 각도·거리는 콘셉트 좌표에서 잰 값
    this.builtHero = view.heroId ?? 'kael'; this.builtJob = view.job?.branch === 1 ? 1 : 0;
    this.make('공격', Btn.Attack, 1, null, 0, 0, false);
    // 스킬 버튼: 자리는 슬롯 칸이 정한다(CELL_POLAR). 습득·페이지 상태에 따라 update가 보이고 숨긴다(처음엔 모두 숨김)
    // c022: 스킬 번호 1..SKILL_MAX를 모두 만들어 두고 영웅이 바뀌면 이름·MP만 다시 쓴다(없는 번호는 습득 마스크가 0이라 늘 숨는다)
    for (let i = 0; i < SKILL_MAX; i++) {
      const s = skillSpecOf(i + 1, this.builtHero, this.builtJob);
      this.make(s?.name ?? '', SKILL_BTN_ALL[i], 0.7, null, i + 1, s?.mpCost ?? 0, true, true);
    }
    this.make('회피', Btn.Dodge, 0.68, [204, 1.05], 0, 0, true);
    this.make('각성', Btn.Awaken, 0.8, [331, 1.04], 0, 0, true);
    // 물약 퀵슬롯(D2c): 회피 버튼 왼쪽, 화면 아래 가장자리(조이스틱 영역 밖·플레이 영역 밖). 자리는 layout이 정한다
    this.potions = new PotionHud(this.root, view, input, 'touch');
    // 쪽 표시 칩: 탭하면 다음 쪽, 좌우로 쓸면 그 방향 쪽(왼쪽으로 쓸면 2쪽, 오른쪽으로 쓸면 1쪽)
    this.chip = el('div', TOUCH_CHIP_CSS, this.root);
    this.chip.dataset.testid = 'touch-skill-page';
    this.chip.setAttribute('role', 'button');
    // 원(지름 44px) 안에 들어가게 쪽 숫자 위·화살표 아래 두 줄
    const chipTop = el('div', 'display:flex;flex-direction:column;align-items:center;gap:1px;line-height:1;', this.chip);
    this.chipPage = el('span', 'font:900 14px ' + FONT_NUM + ';letter-spacing:.3px;color:#ffe07a;' + TEXT_OUTLINE, chipTop, pageText(0));
    el('span', 'font:800 8px ' + FONT + ';color:' + C.gold + ';', chipTop, '◀ ▶');
    this.chipNext = el('div', 'font:700 11px ' + FONT + ';color:#cfd6ee;display:none;', this.chip, '');
    this.chipNext.dataset.testid = 'touch-next-skill';
    this.chip.addEventListener('pointerdown', this.chipDown);
    this.chip.addEventListener('pointerup', this.chipUp);
    this.chip.addEventListener('pointercancel', () => { this.chipPointer = -1; });
    this.layout();
    this.applyZones();
    window.addEventListener('resize', this.onResize);
    window.addEventListener(UI_SIZE_EVENT, this.onResize);
    window.addEventListener('orientationchange', this.onResize);
    window.addEventListener('dh-camera-zoomed', this.onZoomed);
    window.addEventListener('pointerdown', this.pinchDown);
    window.addEventListener('pointermove', this.pinchMove);
    window.addEventListener('pointerup', this.pinchUp);
    window.addEventListener('pointercancel', this.pinchUp);
  }

  dispose(): void {
    this.cameraReset?.remove();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener(UI_SIZE_EVENT, this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    window.removeEventListener('dh-camera-zoomed', this.onZoomed);
    window.removeEventListener('pointerdown', this.pinchDown);
    window.removeEventListener('pointermove', this.pinchMove);
    window.removeEventListener('pointerup', this.pinchUp);
    window.removeEventListener('pointercancel', this.pinchUp);
    this.releaseAll();
    this.root.remove();
  }

  private pinchDown = (e: PointerEvent): void => {
    if (!this.shown || e.pointerType !== 'touch') return;
    const t = e.target as HTMLElement | null;
    if (!t || t.tagName !== 'CANVAS') return;
    if (!loadCameraPrefs().pinch) return;
    // 두 손가락 사이 거리만 쓰므로 화면 회전과 무관하다(실제 화면 좌표 그대로)
    this.pinchPts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this.pinchPts.size === 2) {
      this.pinchStartDist = this.pinchDist();
      this.pinchStartZoom = this.zoomNow;
      this.pinchZoom = Number.NaN;
    }
  };
  private pinchMove = (e: PointerEvent): void => {
    const p = this.pinchPts.get(e.pointerId);
    if (!p) return;
    p.x = e.clientX; p.y = e.clientY;
    if (this.pinchPts.size !== 2 || this.pinchStartDist < 20) return;
    const d = this.pinchDist();
    if (d < 1) return;
    const z = clampZoom(this.pinchStartZoom + (this.pinchStartDist / d - 1) * PINCH_GAIN);
    if (Math.abs(z - this.pinchZoom) < 0.005) return;
    this.pinchZoom = z;
    window.dispatchEvent(new CustomEvent('dh-camera-set', { detail: { zoom: z } }));
  };
  private pinchUp = (e: PointerEvent): void => {
    if (!this.pinchPts.delete(e.pointerId)) return;
    // 두 손가락 중 하나라도 떼면 그 값을 저장하고 끝낸다
    if (!Number.isNaN(this.pinchZoom)) window.dispatchEvent(new CustomEvent('dh-camera-set', { detail: { zoom: this.pinchZoom, save: true } }));
    this.pinchZoom = Number.NaN;
    this.pinchStartDist = 0;
    if (this.pinchPts.size < 2) this.pinchPts.clear();
  };
  private pinchDist(): number {
    const it = this.pinchPts.values();
    const a = it.next().value, b = it.next().value;
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  }

  private make(label: string, bit: BtnBit, k: number, polar: readonly [number, number] | null, skill: number, mpCost: number, caption: boolean, hidden = false): void {
    const f = buildSkillFace(this.root, bit, 'position:absolute;pointer-events:auto;touch-action:none;');
    setFaceHero(f, this.builtHero, this.builtJob);
    const root = f.root;
    // 엄지 고리 배치(2026-10-27): 모든 조작은 원. 뿌리의 border-radius가 눌리는 자리도 원으로 만든다(대각 이웃의 사각 모서리 오입력 방지)
    root.style.borderRadius = '50%';
    if (skill > 0) {
      f.face.style.borderRadius = '50%';
      f.face.style.border = '2px solid rgba(232,200,120,.9)';
      f.face.style.boxShadow = '0 2px 6px rgba(0,0,0,.55)';
      f.sweep.style.borderRadius = f.flash.style.borderRadius = f.cancel.style.borderRadius = f.glint.style.borderRadius = '50%';
    }
    root.dataset.testid = 'touch-' + bit;
    root.setAttribute('role', 'button');
    root.setAttribute('aria-label', label + (skill > 0 ? ' · MP ' + mpCost : ''));
    root.title = label + (skill > 0 ? ' · MP ' + mpCost : '');
    root.setAttribute('aria-pressed', 'false');
    let cap: HTMLDivElement | null = null;
    if (caption && skill === 0) {
      cap = el('div', 'position:absolute;left:50%;bottom:3px;transform:translateX(-50%);font:800 13px ' + FONT + ';color:#fff;' + TEXT_OUTLINE +
        'white-space:nowrap;pointer-events:none;letter-spacing:.5px;', root, label);
    }
    let mpText: HTMLDivElement | null = null;
    if (mpCost > 0 || skill > 0) {
      mpText = el('div', 'position:absolute;right:0;top:4%;padding:1px 6px;border-radius:8px;border:1.5px solid #8ac2ff;' + MP_OK_CSS +
        'font:900 13px ' + FONT_NUM + ';line-height:1;letter-spacing:.5px;box-shadow:0 2px 3px rgba(0,0,0,.7);text-shadow:0 1px 1px #000;pointer-events:none;', root, 'MP');
      mpText.style.display = 'none';
    }
    const ctl: Ctl = { f, mpText, caption: cap, bit, k, polar, skill, mpCost, lastCd: -1, lastTenths: -1, lastMpOk: -1, lastReady: -1, lastCancel: 0, pointer: -1, hidden, cell: -1 };
    if (hidden) root.style.display = 'none';
    root.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      if (ctl.hidden || ctl.pointer !== -1) return;
      ctl.pointer = e.pointerId;
      root.setPointerCapture(e.pointerId);
      this.pressLook(ctl, true);
      this.input.buttonDown(bit);
    });
    const up = (e: PointerEvent): void => {
      if (e.pointerId !== ctl.pointer) return;
      ctl.pointer = -1;
      this.pressLook(ctl, false);
      this.input.buttonUp(bit);
    };
    root.addEventListener('pointerup', up);
    root.addEventListener('pointercancel', up);
    this.ctls.push(ctl);
  }

  /** 영웅이 바뀌었다(c022, 마을에서만): 스킬 버튼 이름·MP를 그 영웅 표로 다시 쓴다 */
  private relabel(hero: PlayableHeroId, job: 0 | 1): void {
    this.builtHero = hero; this.builtJob = job;
    for (const c of this.ctls) {
      setFaceHero(c.f, hero, job);
      if (c.skill === 0) continue;
      const sp = skillSpecOf(c.skill, hero, job);
      c.mpCost = sp?.mpCost ?? 0;
      if (c.caption) c.caption.textContent = sp?.name ?? '';
      c.f.root.setAttribute('aria-label', (sp?.name ?? '') + ' · MP ' + c.mpCost);
      c.f.root.title = (sp?.name ?? '') + ' · MP ' + c.mpCost;
      if (c.mpText) c.mpText.textContent = 'MP';
      c.lastCd = c.lastTenths = c.lastMpOk = c.lastReady = -1;
    }
    this.lastLayout = -1; this.lastNextLv = -1;
  }

  /** 눌림: 버튼 전체가 살짝 작아지고 아이콘이 밝아진다. 놓으면 MP 부족 표시로 되돌린다 */
  private pressLook(c: Ctl, down: boolean): void {
    c.f.root.style.transform = down ? 'scale(.92)' : '';
    c.f.face.style.filter = down ? 'brightness(1.35)' : c.lastMpOk === 0 ? DIM_FILTER : '';
    c.f.root.setAttribute('aria-pressed', down ? 'true' : 'false');
  }

  setScale(s: number): void {
    this.scale = s;
    this.layout();
  }

  /** 버튼 투명도(0.35..1). 누르는 영역은 그대로다 */
  setOpacity(o: number): void {
    this.opacity = Math.min(1, Math.max(0.35, o));
    this.root.style.opacity = String(this.opacity);
  }

  /** 고객 배치를 바꾸고 다시 배치한다(빈 객체 = 자동 배치) */
  setCustomPos(pos: TouchCustomPos): void {
    this.customPos = { ...pos };
    this.layout();
  }

  /**
   * 배치 편집기: 게임을 멈추고 12칸 자리를 끌어 옮긴다. 다른 칸·조이스틱 입력 영역·상단 HUD 띠·각성 게이지와 겹치면 빨간 테두리, 놓으면 직전 자리로 되돌린다.
   * 저장하면 onSave(비율 좌표)를 부르고 바로 적용한다. 실제 버튼 입력과 섞이지 않게 별도 층에서 그린다.
   */
  openEditor(onSave: (pos: TouchCustomPos) => void): void {
    if (this.editor) return;
    this.layout();
    const auto0 = this.autoLayout;
    const host = this.root.parentElement;
    if (!auto0 || !host) return;
    let auto = auto0;
    let W = viewW(), H = viewH(), safe = this.safeNow, fixed = this.fixedNow;
    const wasPaused = this.view.flow.paused;
    this.editorSawPause = wasPaused;
    if (!wasPaused && combatScene(this.view.flow.scene)) this.send?.({ type: 'setPaused', paused: true });
    let work: TouchCustomPos = { ...this.customPos };
    let cur = applyTouchCustom(auto, work, W, H, safe, undefined, fixed);
    const ed = this.editor = el('div', 'position:absolute;inset:0;z-index:60;background:rgba(6,10,22,.55);touch-action:none;pointer-events:auto;font-family:' + FONT + ';', host);
    ed.dataset.testid = 'touch-editor';
    const zone = el('div', 'position:absolute;border:2px dashed rgba(255,120,120,.6);border-radius:10px;background:rgba(255,80,80,.08);pointer-events:none;', ed);
    el('div', 'position:absolute;left:10px;top:8px;font:700 12px ' + FONT + ';color:#ffb3b3;', zone, '이동 조이스틱 영역');
    // 상단 HUD 띠(일시정지·설정 버튼이 있는 곳)도 둘 수 없는 자리로 보여 준다
    const band = el('div', 'position:absolute;left:0;top:0;width:100%;border-bottom:2px dashed rgba(255,120,120,.6);background:rgba(255,80,80,.08);pointer-events:none;', ed);
    // 112px 띠 아래로 내려오는 고정 HUD(태블릿의 카메라 줄, 목표 카드·자동 버튼)도 같은 점선으로 보여 준다
    const fixedBoxes: HTMLDivElement[] = [];
    const sizeBand = (): void => {
      band.style.height = (safe.top + 112) + 'px';
      for (const b of fixedBoxes) b.remove();
      fixedBoxes.length = 0;
      for (const r of fixed) {
        const b = el('div', 'position:absolute;border:2px dashed rgba(255,120,120,.6);border-radius:8px;background:rgba(255,80,80,.08);pointer-events:none;', ed);
        b.style.left = r.x + 'px'; b.style.top = r.y + 'px'; b.style.width = r.w + 'px'; b.style.height = r.h + 'px';
        ed.insertBefore(b, band.nextSibling);
        fixedBoxes.push(b);
      }
    };
    sizeBand();
    el('div', 'position:absolute;right:10px;bottom:6px;font:700 12px ' + FONT + ';color:#ffb3b3;', band, '상단 HUD 영역');
    const bar = el('div', 'position:absolute;left:50%;top:10px;transform:translateX(-50%);display:flex;gap:8px;align-items:center;padding:8px 12px;border-radius:12px;border:2px solid ' + C.gold + ';background:rgba(16,22,42,.95);', ed);
    el('div', 'font:800 14px ' + FONT + ';color:#fff;white-space:nowrap;', bar, '버튼을 끌어 옮기세요');
    const btn = (label: string, id: string, on: () => void): void => {
      const b = el('button', 'min-height:40px;padding:0 12px;border-radius:10px;border:2px solid ' + C.gold + ';background:#1A2238;color:' + C.gold + ';font:800 14px ' + FONT + ';cursor:pointer;', bar, label);
      b.type = 'button'; b.dataset.testid = id;
      b.addEventListener('click', on);
    };
    const names: Record<TouchCustomId, string> = { attack: '공격', dodge: '회피', awaken: '각성', page: '쪽', potion0: '물약1', potion1: '물약2', skill0: '스킬1', skill1: '스킬2', skill2: '스킬3', skill3: '스킬4', skill4: '스킬5', skill5: '스킬6' };
    const ghosts = new Map<TouchCustomId, HTMLDivElement>();
    const paint = (): void => {
      const z = baseJoystickZone(W, H);
      zone.style.left = z.x + 'px'; zone.style.top = z.y + 'px'; zone.style.width = z.w + 'px'; zone.style.height = z.h + 'px';
      for (const id of TOUCH_CUSTOM_IDS) {
        const g = ghosts.get(id)!, r = touchRectOf(cur, id);
        g.style.left = r.x + 'px'; g.style.top = r.y + 'px'; g.style.width = r.w + 'px'; g.style.height = r.h + 'px';
        g.style.borderColor = touchCustomBlocked(cur, id, W, H, safe, undefined, fixed) ? '#ff5a5a' : C.gold;
      }
    };
    for (const id of TOUCH_CUSTOM_IDS) {
      const g = el('div', 'position:absolute;box-sizing:border-box;display:flex;align-items:center;justify-content:center;border-radius:50%;border:3px solid ' + C.gold + ';background:rgba(38,51,90,.85);color:#fff;font:800 12px ' + FONT + ';' + TEXT_OUTLINE + 'cursor:grab;touch-action:none;user-select:none;', ed, names[id]);
      g.dataset.testid = 'touch-editor-' + id;
      let drag = -1, dx = 0, dy = 0, before: TouchCustomPos[TouchCustomId] | undefined;
      g.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        if (drag !== -1) return;
        drag = e.pointerId; g.setPointerCapture(e.pointerId);
        const pt = toView(e.clientX, e.clientY), r = touchRectOf(cur, id);
        dx = pt.x - (r.x + r.w / 2); dy = pt.y - (r.y + r.h / 2);
        before = work[id];
      });
      g.addEventListener('pointermove', (e) => {
        if (e.pointerId !== drag) return;
        const pt = toView(e.clientX, e.clientY), r = touchRectOf(cur, id);
        const moved = touchRectAt(r, (pt.x - dx) / W, (pt.y - dy) / H, W, H, safe);
        work = { ...work, [id]: { x: (moved.x + moved.w / 2) / W, y: (moved.y + moved.h / 2) / H } };
        // 끄는 동안은 겹쳐도 그 자리에 그려 빨간 테두리로 알린다(되돌리기는 놓을 때)
        // 나머지 칸은 끄는 칸과 무관하게 정하고(그 칸이 비운 자리도 쓸 수 있게), 끄는 칸을 그 위에 놓아 판정한다
        cur = touchWithRect(applyTouchCustom(auto, work, W, H, safe, id, fixed), id, moved);
        paint();
      });
      const up = (e: PointerEvent): void => {
        if (e.pointerId !== drag) return;
        drag = -1;
        cur = touchWithRect(applyTouchCustom(auto, work, W, H, safe, id, fixed), id, touchRectOf(cur, id));
        if (touchCustomBlocked(cur, id, W, H, safe, undefined, fixed)) {
          const next = { ...work };
          if (before) next[id] = before; else delete next[id];
          work = next;
        }
        cur = applyTouchCustom(auto, work, W, H, safe, undefined, fixed);
        paint();
      };
      g.addEventListener('pointerup', up);
      g.addEventListener('pointercancel', up);
      ghosts.set(id, g);
    }
    // 열린 채로 화면 크기·방향이 바뀌면: 저장은 비율 좌표라 그대로 두고, 자동 배치·크기·안전영역을 새로 잡아 다시 그린다
    this.editorRefresh = (): void => {
      if (!this.autoLayout) return;
      auto = this.autoLayout; W = viewW(); H = viewH(); safe = this.safeNow; fixed = this.fixedNow;
      cur = applyTouchCustom(auto, work, W, H, safe, undefined, fixed);
      sizeBand(); paint();
    };
    const close = (): void => {
      ed.remove(); this.editor = null; this.editorRefresh = null; this.editorClose = null;
      if (!wasPaused && combatScene(this.view.flow.scene)) this.send?.({ type: 'setPaused', paused: false });
    };
    this.editorClose = close;
    btn('기본 배치', 'touch-editor-reset', () => { work = {}; cur = auto; paint(); });
    btn('취소', 'touch-editor-cancel', close);
    btn('저장', 'touch-editor-save', () => {
      // 겹친 자리는 놓을 때 이미 되돌렸으므로 work에는 겹치지 않는 자리만 있다
      const saved: TouchCustomPos = {};
      for (const id of TOUCH_CUSTOM_IDS) if (work[id]) saved[id] = work[id];
      this.setCustomPos(saved);
      onSave(saved);
      close();
    });
    paint();
  }

  /** 오른쪽 가장자리의 고정 6칸. 이름·MP 설명은 접근성 이름과 수동 툴팁으로 제공한다. */
  layout(): void {
    const W = viewW(), H = viewH();
    const st = getComputedStyle(this.root);
    const safe = { left: parseFloat(st.paddingLeft) || 0, right: parseFloat(st.paddingRight) || 0, top: parseFloat(st.paddingTop) || 0, bottom: parseFloat(st.paddingBottom) || 0 };
    const auto = touchHudLayout(W, H, this.scale * uiSizeFactor(W), safe);
    this.autoLayout = auto; this.safeNow = safe;
    this.fixedNow = fixedHudRects(W, H, designScale(W, H) * uiSizeFactor(W));
    const p = applyTouchCustom(auto, this.customPos, W, H, safe, undefined, this.fixedNow);
    // 환생 추가 칸(7번째, 레인 R): 기존 배치가 끝난 뒤 빈 자리. 없으면 그 칸은 숨긴다
    this.seventh = seventhTouchRect(p, W, H, safe, this.fixedNow);
    const rects = [...p.skills, p.attack, p.dodge, p.awaken, p.page, ...p.potions, p.gauge];
    if (this.seventh) rects.push(this.seventh);
    this.controlRects = rects; this.layoutVersion++;
    for (const c of this.ctls) {
      const r = c.skill > 0 ? (c.cell >= 0 ? (c.cell < 6 ? p.skills[c.cell] : this.seventh) : null) : c.bit === Btn.Attack ? p.attack : c.bit === Btn.Dodge ? p.dodge : p.awaken;
      if (r) this.placeRect(c.f.root, r);
      c.f.cdText.style.fontSize = Math.max(15, Math.round(p.size * .3)) + 'px';
      if (c.caption) c.caption.style.fontSize = '11px';
    }
    this.placeRect(this.chip, p.page);
    this.chipNext.style.display = 'none';
    for (const i of [0, 1] as const) this.potions.place(i, p.potions[i].x + p.potions[i].w / 2, p.potions[i].y + p.potions[i].h / 2, p.potions[i].w);
    const g = this.gaugeAnchor;
    g.on = true; g.x = p.gauge.x + p.gauge.w / 2; g.bottom = H - p.gauge.y - p.gauge.h; g.width = p.gauge.w;
    this.stickR = p.joystick.w / 2;
    this.stickMinX = p.joystick.x + this.stickR;
    this.stickMinY = safe.top + this.stickR + 8;
    this.stickBottom = safe.bottom + 12;
    const d = this.stickR * 2;
    this.base.style.width = this.base.style.height = d + 'px';
    this.base.style.margin = -this.stickR + 'px 0 0 ' + -this.stickR + 'px';
  }

  /** 실제로 놓은 조작 버튼 사각형(사용자 크기·지정 위치 반영, 물약·쪽 표시·각성 게이지 포함). layout()이 다시 계산할 때만 새로 만든다 */
  controlRects: readonly HudRect[] = [];
  /** layout()이 끝날 때마다 오른다: 입구 선택 띠 등 다른 HUD가 조작 배치가 바뀐 것을 숫자 비교로 안다 */
  layoutVersion = 0;

  private placeRect(element: HTMLElement, r: HudRect): void {
    element.style.left = r.x + 'px'; element.style.top = r.y + 'px';
    element.style.width = r.w + 'px'; element.style.height = r.h + 'px';
  }

  private chipDown = (e: PointerEvent): void => {
    e.preventDefault();
    if (this.chipPointer !== -1) return;
    this.chipPointer = e.pointerId;
    this.chipX0 = toView(e.clientX, e.clientY).x;
    this.chip.setPointerCapture(e.pointerId);
    this.chip.style.transform = 'scale(.94)';
  };
  /** 탭(거의 안 움직임) = 다음 쪽, 좌우로 36px 이상 쓸기 = 왼쪽이면 2쪽·오른쪽이면 1쪽 */
  private chipUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.chipPointer) return;
    this.chipPointer = -1;
    this.chip.style.transform = '';
    const dx = toView(e.clientX, e.clientY).x - this.chipX0;
    if (Math.abs(dx) >= SWIPE_PX) this.send?.({ type: 'setSkillPage', page: dx < 0 ? 1 : 0 });
    else this.send?.({ type: 'setSkillPage' });
  };

  /** 조이스틱 영역 조각을 퀘스트 카드 사각형을 피해 다시 놓는다. 화면·카드가 바뀔 때만 DOM을 쓴다 */
  private applyZones(): void {
    const W = viewW(), H = viewH();
    // 퀘스트 카드와 그 아래 골드·가방 줄(둘 다 누를 수 있다)을 한 덩어리로 피한다
    const card: Rect | null = hudBlockRect(villageHudLayout.rect, villageHudLayout.row);
    this.zoneVer = villageHudLayout.version;
    const list = joystickZones(W, H, card);
    const key = W + 'x' + H + '|' + list.map((r) => r.x.toFixed(1) + ',' + r.y.toFixed(1) + ',' + r.w.toFixed(1) + ',' + r.h.toFixed(1)).join(';');
    if (key === this.zoneKey) return;
    this.zoneKey = key;
    this.zones.forEach((z, i) => {
      const r = list[i];
      if (!r) { z.style.display = 'none'; return; }
      z.style.display = 'block';
      z.style.left = r.x + 'px'; z.style.top = r.y + 'px'; z.style.width = r.w + 'px'; z.style.height = r.h + 'px';
    });
  }

  private stickDown = (e: PointerEvent): void => {
    if (this.stickPointer !== -1) return;
    e.preventDefault();
    this.stickPointer = e.pointerId;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    // 게임 화면 좌표(세로로 든 기기에서 #app을 돌렸으면 돌린 좌표)로 받는다: 조이스틱 방향이 화면에 보이는 방향과 맞는다
    const p = toView(e.clientX, e.clientY);
    this.stickStartX = p.x;
    this.stickStartY = p.y;
    this.stickCx = Math.max(this.stickMinX, Math.min(viewW() / 3 - this.stickR - 12, p.x));
    this.stickCy = Math.max(this.stickMinY, Math.min(viewH() - this.stickR - this.stickBottom, p.y));
    this.base.style.display = this.knob.style.display = 'block';
    this.base.style.left = this.knob.style.left = this.stickCx + 'px';
    this.base.style.top = this.knob.style.top = this.stickCy + 'px';
  };
  private stickMove = (e: PointerEvent): void => {
    if (e.pointerId !== this.stickPointer) return;
    const p = toView(e.clientX, e.clientY);
    const m = floatingStickMotion(this.stickStartX, this.stickStartY, p.x, p.y, this.stickR);
    this.knob.style.left = this.stickCx + m.visualX + 'px';
    this.knob.style.top = this.stickCy + m.visualY + 'px';
    this.input.setStick(m.x, m.y);
  };
  private stickUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.stickPointer) return;
    this.stickPointer = -1;
    this.base.style.display = this.knob.style.display = 'none';
    this.input.setStick(0, 0);
  };

  /** 눌린 입력을 모두 놓는다(일시정지·장면 전환). */
  releaseAll(): void {
    this.stickPointer = -1;
    this.base.style.display = this.knob.style.display = 'none';
    this.input.setStick(0, 0);
    for (const c of this.ctls) {
      if (c.pointer !== -1) { c.pointer = -1; this.pressLook(c, false); this.input.buttonUp(c.bit); }
    }
  }

  update(enabled: boolean): void {
    const v = this.view;
    const sc = v.flow.scene;
    // 배치 편집 중 키보드 Esc·패드 Start로 던전이 재개되면 편집기를 닫는다(취소). 편집기는 화면 입력을 모두 가로채므로
    // 열어 둔 채 전투가 다시 돌면 고객이 터치 조작 없이 맞기만 한다. 여는 직후 일시정지가 반영되기 전 틱은 건너뛴다.
    if (this.editor) {
      if (!combatScene(sc)) this.editorSawPause = false;
      else if (v.flow.paused) this.editorSawPause = true;
      else if (this.editorSawPause) this.editorClose?.();
    }
    this.glintReq = false;
    this.cursor = v.events.readFrom(this.cursor, this.onEvent);
    // NPC 창·장비 창이 열려 있는 동안은 조이스틱·버튼을 숨겨 뒤 게임으로 입력이 새지 않게 한다(숨기면 눌린 입력도 모두 놓는다)
    const show = enabled && (sc === Scene.Village || combatScene(sc)) && !v.flow.paused && v.village.menuNpc < 0 && !v.village.inventoryOpen && !v.village.stageMapOpen && !v.village.questOpen && !v.village.rebirthOpen;
    if (show !== this.shown) {
      this.shown = show;
      this.root.style.display = show ? 'block' : 'none';
      if (!show) this.releaseAll();
    }
    this.potions.update(show && combatScene(sc));
    if (!show) return;
    // 조이스틱을 잡고 있는 동안은 영역을 바꾸지 않는다(잡은 조각이 숨겨져 손가락 이벤트가 끊기는 것을 막는다)
    if (villageHudLayout.version !== this.zoneVer && this.stickPointer === -1) this.applyZones();
    const h = v.hero;
    const combat = combatScene(sc);
    // 습득(Lv5 천공 올려베기·Lv10 진홍 초승달 자동 습득)하고 현재 페이지에 놓인 스킬만 보인다. 슬롯 배치·페이지·습득이 바뀌면 숨김 상태와 배치를 다시 맞춘다
    const hero = v.heroId ?? 'kael', job: 0 | 1 = v.job?.branch === 1 ? 1 : 0;
    if (hero !== this.builtHero || job !== this.builtJob) this.relabel(hero, job);
    const g = growthOf(v);
    const learned = learnedMaskOf(g);
    const sv = slotsOf(v);
    const key = layoutKey(sv, learned);
    const layoutChanged = key !== this.lastLayout;
    if (layoutChanged) {
      this.lastLayout = key;
      for (const c of this.ctls) {
        if (c.skill === 0) continue;
        const on = skillShown(sv, learned, c.skill);
        c.cell = on ? cellOfSkill(sv, c.skill) : -1;
        if (on === c.hidden) {
          c.hidden = !on;
          if (c.hidden && c.pointer !== -1) { c.pointer = -1; this.pressLook(c, false); this.input.buttonUp(c.bit); }
          // 처음 보이는 순간 준비 반짝임이 터지지 않게 상태를 처음으로 되돌린다
          c.lastCd = c.lastTenths = c.lastMpOk = c.lastReady = -1;
        }
      }
      this.chipPage.textContent = pageText(sv.page);
      this.chip.setAttribute('aria-label', '스킬 슬롯 ' + pageText(sv.page) + '쪽, 탭하거나 좌우로 쓸어 넘기기');
      this.layout();
      // 7번째 칸 자리가 없는 화면이면 그 스킬 버튼은 숨긴다(입력은 키 7·서랍 배치로 남는다)
      for (const c of this.ctls) if (c.cell === 6 && !this.seventh && !c.hidden) { c.hidden = true; if (c.pointer !== -1) { c.pointer = -1; this.pressLook(c, false); this.input.buttonUp(c.bit); } }
    }
    const nl = g ? g.level : 0;
    if (nl !== this.lastNextLv) {
      this.lastNextLv = nl;
      const t = g ? nextSkillText(g.level, hero) : '';
      this.chipNext.textContent = t;
      this.chipNext.style.display = 'none';
      this.chip.title = '스킬 페이지 전환' + (t ? ' · ' + t : '');
    }
    if (combat !== this.combatShown || layoutChanged) {
      this.combatShown = combat;
      for (const c of this.ctls) {
        c.f.root.style.display = combat && !c.hidden ? 'block' : 'none';
        if (!combat && c.pointer !== -1) { c.pointer = -1; this.pressLook(c, false); this.input.buttonUp(c.bit); }
      }
      this.chip.style.display = combat ? 'flex' : 'none';
    }
    if (!combat) return;
    const lk = v.link;
    const cancelBits = lk ? lk.cancelReady : 0;
    const now = this.glintReq ? performance.now() : 0;
    for (const c of this.ctls) {
      if (c.hidden) continue;
      if (c.skill > 0) {
        const i = c.skill - 1;
        // 칼날의 박자(c017): 재사용 대기 중인 칸에 시계 바늘 반짝임
        if (this.glintReq && h.cooldowns[i] > 0) glintFace(c.f, now, v.settings.reduceFlash);
        // 캔슬 가능 표시(PC 스킬 바와 같은 비트·같은 부품). 바뀔 때만 쓴다
        const cancelOn = (cancelBits >> i) & 1;
        if (cancelOn !== c.lastCancel) { c.lastCancel = cancelOn; setCancelMark(c.f, cancelOn === 1); }
        const q = sweepStep(h.cooldowns[i], h.cooldownMax[i]);
        if (q !== c.lastCd) { c.lastCd = q; c.f.sweep.style.background = CD_SWEEP[q]; }
        const t = cdTenths(h.cooldowns[i]);
        if (t !== c.lastTenths) { c.lastTenths = t; c.f.cdText.textContent = CD_TEXT[t]; }
        const mpOk = h.mp >= effectiveMpCost(c.mpCost, h.mpCostCut) ? 1 : 0;
        if (mpOk !== c.lastMpOk) {
          c.lastMpOk = mpOk;
          if (c.pointer === -1) c.f.face.style.filter = mpOk ? '' : DIM_FILTER;
          if (c.mpText) { setMpBadge(c.mpText, mpOk === 1); c.mpText.style.display = mpOk ? 'none' : 'block'; }
        }
        const ready = q === 0 && mpOk === 1 ? 1 : 0;
        if (ready !== c.lastReady) {
          if (ready === 1 && c.lastReady === 0) c.f.flash.animate(READY_KF, READY_OPT);
          c.lastReady = ready;
          c.f.root.dataset.ready = String(ready); c.f.root.dataset.mp = mpOk ? 'ok' : 'low';
        }
      } else if (c.bit === Btn.Awaken) {
        const ready = h.awakenGauge >= 1;
        const q = ready ? -1 : Math.floor(h.awakenGauge * 100);
        if (q !== c.lastCd) {
          c.lastCd = q;
          // 채워지지 않은 부분만 어둡게(PC와 같은 표: 100 = 다 참)
          c.f.sweep.style.background = ready ? 'none' : AW_SWEEP[q];
          c.f.face.style.animation = ready ? AW_READY_ANIM : 'none';
          if (ready && c.lastReady === 0) c.f.flash.animate(READY_KF, READY_OPT);
          c.lastReady = ready ? 1 : 0;
          c.f.cdText.textContent = ready ? '준비' : ''; c.f.root.dataset.ready = ready ? '1' : '0';
        }
      } else if (c.bit === Btn.Dodge) {
        const q = h.dodgeCooldown > 0 ? 1 : 0;
        if (q !== c.lastCd) { c.lastCd = q; c.f.sweep.style.background = q ? 'rgba(6,8,18,.45)' : 'none'; }
      }
    }
  }
}
